/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import * as d3 from 'd3';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTimeScale } from 'src/components/charts/helpers/useScale';
import { format } from 'src/lib/format';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';

import { Cursor, useCursorUpdate } from './helpers/Cursor';
import { SvgWrapper, useSvgLayout } from './helpers/svgLayout';
import { useCursorDateIndex } from './helpers/useCursorDateIndex';
import { StackConfig } from './helpers/useStackInfo';

const DOT_RADIUS = 4;
const FOCUS_RING_SIZE = 4;
export function DotsTimeline<Data>({
  className,
  stack,
  getMaxDate,
  minDate: minDateProp,
}: {
  className?: string;
  stack: StackConfig<Data, { timestamp: Timestamp['output'] }>;
  getMaxDate?: (maxDate: Date) => { date: Date; poll: boolean };
  minDate: number;
}) {
  const { setFirstTick, ...svgLayout } = useSvgLayout({
    left: DOT_RADIUS + FOCUS_RING_SIZE,
    right: DOT_RADIUS + FOCUS_RING_SIZE,
    top: FOCUS_RING_SIZE,
    bottom: FOCUS_RING_SIZE,
  });
  const { y, width } = svgLayout;

  const series = useMemo(
    () =>
      stack.data.map((d) => ({
        timestamp: new Date(stack.getValue(d).timestamp),
      })),
    [stack],
  );

  const [dataMinDate, dataMaxDate] = useMemo(
    () => d3.extent(series, (bucket) => bucket.timestamp) as [Date, Date],
    [series],
  );
  const [maxDate, setMaxDate] = useState(
    () => getMaxDate?.(dataMaxDate).date ?? dataMaxDate,
  );
  useEffect(() => {
    if (getMaxDate) {
      // This allows us to have the timeline grow as time progresses and show a live view
      let id: NodeJS.Timeout | null = setInterval(() => {
        const { date, poll } = getMaxDate(dataMaxDate);
        setMaxDate(date ?? dataMaxDate);
        if (!poll && id) {
          clearInterval(id);
          id = null;
        }
      }, 1000 / 60);
      return () => {
        if (id) clearInterval(id);
        id = null;
      };
    } else {
      setMaxDate(dataMaxDate);
    }
  }, [dataMaxDate, getMaxDate]);

  const minDate = useMemo(
    () => moment.min([moment(minDateProp), moment(dataMinDate)]).toDate(),
    [minDateProp, dataMinDate],
  );
  const xScale = useTimeScale({
    range: [0, width],
    domain: [minDate, maxDate],
  });

  const cursorValueIndex = useCursorDateIndex({
    data: series,
    getTimestamp: (bucket: { timestamp: Date }) => bucket.timestamp,
  });
  const cursorValue = useMemo(() => {
    if (cursorValueIndex === null) return null;
    const value = series[cursorValueIndex];
    if (!value) return null;

    return {
      timestamp: value.timestamp,
      x: xScale(value.timestamp),
      y: 0,
      primaryColor: stack.primaryColor,
      tooltipContent: (
        <div className="text-xs">
          {format.ms(moment(value.timestamp).diff(moment(minDate)))}
        </div>
      ),
    };
  }, [cursorValueIndex, minDate, series, stack.primaryColor, xScale]);
  const getCursor = useCallback(
    (n: [number, number]) => ({ date: moment(xScale.invert(n[0])) }),
    [xScale],
  );
  const onCursorChange = useCursorUpdate();

  return (
    <SvgWrapper
      onMouseEnter={(e) => {
        // clear out any tab focus states when the mouse is changing the cursor
        e.currentTarget.querySelector<HTMLAnchorElement>('a:focus')?.blur();
      }}
      className={classnames(className, 'h-5')}
      css={css`
        svg {
          display: block;
        }
      `}
      getCursor={getCursor}
      {...svgLayout}
    >
      <line
        className="stroke-border-primary"
        x2={xScale(maxDate)}
        transform={`translate(0, ${y + DOT_RADIUS})`}
      />
      {series.map(({ timestamp: value }, i) => {
        return (
          <a
            {...{ xlinkHref: '#' }}
            // eslint-disable-next-line react/no-array-index-key
            key={`${String(value)}${i}`}
            onFocus={() => onCursorChange({ date: moment(value) })}
            onBlur={() => onCursorChange({})}
          >
            <circle
              transform={`translate(${xScale(value)}, ${y + DOT_RADIUS})`}
              r={DOT_RADIUS}
              strokeWidth={DOT_RADIUS}
              className="fill-icon-info"
              stroke="transparent"
            />
          </a>
        );
      })}
      <Cursor cursorValue={cursorValue} width={width} />
    </SvgWrapper>
  );
}
